import React, { useContext } from 'react';
import { useMutation, useQuery } from 'react-apollo';

import {
	ContentTypeType,
	useEditorAnalyticsEvent,
	useEditorBreadcrumbs,
	getNearestVisibleAncestor,
} from '@confluence/editor-features';
import { SessionData } from '@confluence/session-data';
import { SPACE_OVERVIEW } from '@confluence/named-routes';
import { RoutesContext, useNavigationPolicy } from '@confluence/route-manager';
import { useBlogTreeActions } from '@confluence/blog-tree-store/entry-points/useBlogTreeStore';

import { EditorRevertDialogComponent } from './EditorRevertDialogComponent';
import type { EditorRevertDialogProps } from './types';
import { useContentRelations } from './hooks/useContentRelations';
import { useEditorRevertDialogContext } from './EditorRevertDialogContext';
import { RevertContentMutation } from './RevertContentMutation.experimentalgraphql';
import { DeleteDraftMutationForRevertDialog } from './DeleteDraftMutationForRevertDialog.graphql';
import type { RevertContentMutation as RevertContentMutationType } from './__types__/RevertContentMutation';
import type { DeleteDraftMutationForRevertDialog as DeleteDraftMutationType } from './__types__/DeleteDraftMutationForRevertDialog';
import { EditorRevertDialogContentQuery } from './EditorRevertDialogContentQuery.graphql';
import { getBlueprintType } from './utilities';
import type { EditorRevertDialogContentQuery as EditorRevertDialogContentQueryType } from './__types__/EditorRevertDialogContentQuery';

export const EditorRevertDialog = (props: Omit<EditorRevertDialogProps, 'content'>) => {
	const { contentId, isUnpublishedDraft } = props;
	const { data, loading, error } = useQuery<EditorRevertDialogContentQueryType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		EditorRevertDialogContentQuery,
		{
			variables: {
				contentId,
				status: [isUnpublishedDraft ? 'draft' : 'current'],
			},
		},
	);

	if (loading || error) {
		return null;
	}

	const content = (data?.content?.nodes ?? [])[0] ?? null;

	return <EditorRevertDialogContainer {...props} content={content} />;
};

const EditorRevertDialogContainer = (props: EditorRevertDialogProps) => {
	const {
		relations: collaborators,
		loading: fetchingCollaboratorRelations,
		error: errorFetchingCollaborators,
	} = useContentRelations('collaborator', props.contentId, props.isUnpublishedDraft);
	const {
		relations: touched,
		loading: fetchingTouchedRelations,
		error: errorFetchingTouched,
	} = useContentRelations('touched', props.contentId, props.isUnpublishedDraft);
	const analyticsData = {
		blueprintType: getBlueprintType(props.content),
		contentId: props.contentId,
		contentType: props.content?.type ?? '',
		spaceKey: props.spaceKey,
		version: props.content?.version?.number ?? undefined,
		source: 'next-EditorRevertDialogComponent',
		subject: 'revertOrDeleteDialog',
	};
	const { triggerAnalyticsEvent } = useEditorAnalyticsEvent(analyticsData);
	const { forceRefetchBlogTree } = useBlogTreeActions();

	const { matchSupportedRoute, push, replace, match } = useContext(RoutesContext);
	const { routePolicy } = useNavigationPolicy();
	const shouldPushInSelf = (path) => {
		return (
			!routePolicy ||
			routePolicy.willPushInSelf(match?.name, match?.params.contentId, path, undefined)
		);
	};

	const {
		shouldShowRevertDialog,
		shouldShowRevertDialogDiff,
		hideRevertDialogDiff,
		showRevertDialogDiff,
		hideRevertDialog,
	} = useEditorRevertDialogContext();
	const mutationVars = { contentId: props.contentId };
	const [revertContent] = useMutation<RevertContentMutationType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Experimental Schema GraphQL Operation
		RevertContentMutation,
		{
			variables: mutationVars,
			onCompleted: (data) => {
				if (data.experimentalRevertContent) {
					hideRevertDialog();
					const viewPagePath =
						(props.content?.links?.context ?? '') + (props.content?.links?.webui ?? '');
					props.cancelDebouncedSaveDraft?.();
					if (shouldPushInSelf(matchSupportedRoute(viewPagePath))) {
						if (viewPagePath !== '') {
							push(viewPagePath);
						}
					}
				}
			},
		},
	);
	const [deleteContent] = useMutation<DeleteDraftMutationType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		DeleteDraftMutationForRevertDialog,
		{
			variables: mutationVars,
			onCompleted: (data) => {
				if (data.deleteContent.contentId) {
					const route = getAncestorRoute(props.spaceKey);
					hideRevertDialog();
					props.cancelDebouncedSaveDraft?.();
					if (shouldPushInSelf(route)) {
						replace(route);
					}

					if (props.content?.type === ContentTypeType.BLOGPOST) {
						// update blog tree after unpublished blog draft is deleted
						forceRefetchBlogTree();
					}
				}
			},
		},
	);

	const errorFetchingRelations =
		errorFetchingCollaborators !== undefined || errorFetchingTouched !== undefined;

	const breadcrumbs = useEditorBreadcrumbs(props.contentId);
	const getAncestorRoute = (spaceKey) => {
		// Gets the nearest ancestor in breadcrumbs. Use space index as fallback route in case of error or if page is a blog
		let url = SPACE_OVERVIEW.toUrl({ spaceKey });
		if (props.content?.type === ContentTypeType.PAGE && breadcrumbs) {
			url = getNearestVisibleAncestor({
				breadcrumbs,
				fallbackUrl: url,
			});
		}
		return url;
	};

	return (
		<SessionData>
			{(sessionData) => (
				<EditorRevertDialogComponent
					content={props.content}
					contentId={props.contentId}
					isUnpublishedDraft={props.isUnpublishedDraft}
					triggerAnalyticsEvent={triggerAnalyticsEvent}
					collaboratorRelations={collaborators}
					touchedRelations={touched}
					fetchingCollaboratorRelations={fetchingCollaboratorRelations}
					fetchingTouchedRelations={fetchingTouchedRelations}
					shouldShowRevertDialog={shouldShowRevertDialog}
					shouldShowRevertDialogDiff={shouldShowRevertDialogDiff}
					hideRevertDialogDiff={hideRevertDialogDiff}
					showRevertDialogDiff={showRevertDialogDiff}
					hideRevertDialog={hideRevertDialog}
					errorFetchingRelations={errorFetchingRelations}
					revertContent={revertContent}
					deleteContent={deleteContent}
					currentAccountId={sessionData.userId}
					contentType={props.content?.type as ContentTypeType}
				/>
			)}
		</SessionData>
	);
};
